import React from "react";
import { Check, Sparkles, ArrowRight, Rocket, Mail } from "lucide-react";

const TIERS = [
    {
        name: "Starter",
        subtitle: "Scout",
        price: "₹8,000",
        cadence: "/ month",
        tagline:
            "For micro food / agri processors needing basic feasibility.",
        features: [
            "1 research question / month",
            "Desk research + literature review",
            "Regulatory checklist (FSSAI / APEDA)",
            "5 hrs consultation / support per month",
        ],
        tag: null,
        highlight: false,
    },
    {
        name: "Cultivate",
        subtitle: "Grow",
        price: "₹22,000",
        cadence: "/ month",
        tagline: "Growing bio-enterprises needing lab + data support.",
        features: [
            "Up to 3 active projects",
            "Bench-scale lab work",
            "Formulation or bioprocess support",
            "Monthly report + IP advisory",
            "Priority turnaround (5 days)",
        ],
        tag: null,
        highlight: false,
    },
    {
        name: "Accelerate",
        subtitle: "Scale-up",
        price: "₹55,000",
        cadence: "/ month",
        tagline: "Established MSMEs doing product development.",
        features: [
            "Unlimited queries",
            "Pilot-scale trials",
            "Co-invention framework",
            "Regulatory filing support",
            "Dedicated researcher allocation",
        ],
        tag: null,
        highlight: false,
    },
    {
        name: "Partner",
        subtitle: "Enterprise",
        price: "Custom",
        cadence: "pricing",
        tagline: "Pharma / agri-exporters needing deep R&D alliance.",
        features: [
            "Joint product development",
            "Shared IP / royalty model",
            "Embedded researcher on-site",
            "Grant co-application (BIRAC, DBT)",
        ],
        tag: null,
        highlight: false,
    },
];

const TierCard = ({ t }) => (
    <article
        data-testid={`pricing-card-${t.name.toLowerCase()}`}
        className={`group relative flex flex-col h-full rounded-[1.75rem] border bg-card/60 backdrop-blur-sm p-7 md:p-8 overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 ${
            t.highlight
                ? "border-primary/70 shadow-xl shadow-primary/10 ring-1 ring-primary/40"
                : "border-border hover:border-primary/60"
        }`}
    >
        {/* corner glow */}
        <div
            aria-hidden
            className="pointer-events-none absolute -top-20 -right-20 h-44 w-44 rounded-full bg-primary/15 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        />

        {/* tag */}
        {t.tag && (
            <span
                className={`absolute top-5 right-5 inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold ${
                    t.highlight
                        ? "bg-primary text-primary-foreground shadow"
                        : "bg-card border border-border text-muted-foreground"
                }`}
            >
                {t.highlight && <Sparkles className="h-3 w-3" />}
                {t.tag}
            </span>
        )}

        <div className="relative">
            <p className="text-[11px] uppercase tracking-[0.22em] text-primary font-semibold">
                {t.subtitle}
            </p>
            <h3 className="mt-2 font-display text-3xl md:text-[34px] font-medium tracking-tight">
                {t.name}
            </h3>
            <p className="mt-3 text-[13.5px] leading-relaxed text-muted-foreground min-h-[42px]">
                {t.tagline}
            </p>

            <div className="mt-6 flex items-baseline gap-1.5">
                <span className="font-display text-4xl md:text-5xl font-medium tracking-tight">
                    {t.price}
                </span>
                <span className="text-[12px] uppercase tracking-[0.22em] text-muted-foreground">
                    {t.cadence}
                </span>
            </div>

            <div className="mt-6 h-px w-12 bg-border group-hover:w-20 group-hover:bg-primary transition-all duration-500" />

            <ul className="mt-6 space-y-2.5">
                {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13.5px] leading-snug text-foreground/85">
                        <Check className="h-4 w-4 mt-[2px] text-primary shrink-0" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
        </div>

        <div className="mt-auto pt-7">
            <a
                href="mailto:founders.yaalee@gmail.com"
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className="w-full flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[13px] font-medium bg-primary text-primary-foreground shadow-md hover:bg-primary/90 transition"
            >
                <span>Get started</span>
                <ArrowRight className="h-4 w-4" />
            </a>
        </div>
    </article>
);

export const Pricing = () => {
    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="section-pad relative overflow-hidden bg-card/40"
        >
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-copper))]/12"
                style={{ width: 580, height: 580, top: -160, right: -140 }}
            />
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-moss))]/12"
                style={{ width: 520, height: 520, bottom: -200, left: -160 }}
            />

            <div className="container-x relative">
                {/* Header */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
                    <div className="lg:col-span-7">
                        <p className="overline">
                            <span className="eyebrow-line" />
                            Pricing
                        </p>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
                            Plans for every{" "}
                            <em className="not-italic text-primary">stage</em>{" "}
                            of bio-innovation.
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-base md:text-lg leading-relaxed text-muted-foreground">
                        Transparent, India-rupee pricing for MSME bio-industry clients —
                        from one-question feasibility scouts to full R&D alliances.
                    </p>
                </div>

                {/* Main 4-tier grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                    {TIERS.map((t) => (
                        <TierCard key={t.name} t={t} />
                    ))}
                </div>

                {/* Startup support package — separate banner */}
                <div
                    data-testid="pricing-startup-card"
                    className="mt-12 relative overflow-hidden rounded-[1.75rem] border border-border bg-card/60 backdrop-blur-sm"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-primary/15 blur-3xl"
                    />
                    <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 p-7 md:p-10 items-center">
                        <div className="lg:col-span-5">
                            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/40 px-3 py-1.5">
                                <Rocket className="h-3.5 w-3.5 text-primary" />
                                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-primary">
                                    Startup support package
                                </span>
                            </div>
                            <h3 className="mt-4 font-display text-3xl md:text-4xl font-medium tracking-tight leading-tight">
                                Startup mentorship &{" "}
                                <em className="not-italic text-primary">feasibility</em>.
                            </h3>
                            <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground max-w-md">
                                For early-stage founders, student innovators, and
                                biotech startups taking their first steps.
                            </p>

                            <div className="mt-6 flex items-baseline gap-1.5">
                                <span className="font-display text-5xl font-medium tracking-tight">
                                    ₹4,000
                                </span>
                                <span className="text-[12px] uppercase tracking-[0.22em] text-muted-foreground">
                                    / month
                                </span>
                            </div>

                            <a
                                href="mailto:founders.yaalee@gmail.com"
                                data-testid="pricing-startup-cta"
                                className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-md hover:bg-primary/90 transition"
                            >
                                Apply now
                                <ArrowRight className="h-4 w-4" />
                            </a>
                        </div>

                        <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 lg:border-l lg:border-border lg:pl-10">
                            {[
                                "Mentorship & strategic guidance",
                                "Idea feasibility evaluation",
                                "Basic market validation inputs",
                                "Product / research roadmap suggestions",
                                "Monthly consultation support",
                            ].map((f) => (
                                <li
                                    key={f}
                                    className="flex items-start gap-2.5 text-[14px] leading-snug text-foreground/85"
                                >
                                    <Check className="h-4 w-4 mt-[2px] text-primary shrink-0" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Custom plan footer strip */}
                <div className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-5 rounded-[1.25rem] border border-border bg-card/50 backdrop-blur-sm px-7 py-6">
                    <div>
                        <p className="font-display text-xl md:text-2xl tracking-tight">
                            Need a customised plan?
                        </p>
                        <p className="mt-1 text-[13.5px] text-muted-foreground">
                            Negotiate pricing or build a tailored engagement model with us.
                        </p>
                    </div>
                    <a
                        href="mailto:founders.yaalee@gmail.com"
                        data-testid="pricing-custom-contact"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-md hover:bg-primary/90 transition whitespace-nowrap"
                    >
                        <Mail className="h-4 w-4" />
                        Contact us
                    </a>
                </div>
            </div>
        </section>
    );
};

export default Pricing;
